import { Alert, AlertDescription } from "@repo/ui/alert";
import { Badge } from "@repo/ui/badge";
import { Button } from "@repo/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@repo/ui/card";
import {
  Bar,
  BarChart,
  CartesianGrid,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  Line,
  LineChart,
  XAxis,
  YAxis,
} from "@repo/ui/chart";
import { Input } from "@repo/ui/input";
import { Label } from "@repo/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@repo/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@repo/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@repo/ui/tabs";
import {
  Activity,
  AlertCircle,
  BarChart3,
  DatabaseZap,
  Eye,
  Loader2,
  Play,
  RotateCw,
  Search,
  Trash2,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { RunDetailDrawer } from "@/components/valuations/RunDetailDrawer";
import { TraceResultDialog } from "@/components/valuations/TraceResultDialog";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";
import type {
  SuburbRecord,
  ValuationAlgorithm,
  ValuationRun,
  ValuationSourceCoverage,
  ValuationSourceMode,
  ValuationSuburbMonthlyResult,
} from "@/lib/api";
import {
  DEFAULT_VALUATION_ALGORITHM,
  deleteValuationRun,
  fetchDefaultValuationSuburb,
  fetchValuationAlgorithms,
  fetchValuationRunResults,
  fetchValuationRuns,
  fetchValuationSourceCoverage,
  readSourceSelectionFromRun,
  replayValuationRun,
  runMonthlyMovement,
  searchValuationSuburbs,
} from "@/lib/api";

const WHOLE_PAGE_TABS = [
  { value: "monthly", label: "PV Suburb Movements Monthly", status: "ready" },
  { value: "weighted-comps", label: "Weighted Comps", status: "todo" },
  { value: "macro-signals", label: "Macro Signals", status: "todo" },
  { value: "hedonic", label: "Hedonic Index", status: "todo" },
  { value: "repeat-sales", label: "Repeat Sales", status: "todo" },
  { value: "hybrid", label: "Hybrid Model", status: "todo" },
  { value: "property-pv", label: "Property PV", status: "todo" },
];

const FILTER_OPTIONS = ["Any", "1", "2", "3", "4", "5", "6+"];

interface SourceModeOption {
  value: ValuationSourceMode;
  label: string;
  description: string;
}

// Source-selection options surfaced in the run controls. All modes read
// from property_snapshots; the filter is applied via adapter_slug.
// "adapter" is reserved for future per-adapter sourcing (UI not wired).
const SOURCE_MODE_OPTIONS: SourceModeOption[] = [
  {
    value: "all",
    label: "All sources",
    description: "Confirmed scraped house sales; untyped SQM sales are excluded",
  },
  {
    value: "internal_only",
    label: "Scraped only",
    description: "property_snapshots excluding adapter_slug=sqm-back-history",
  },
  {
    value: "sqm_only",
    label: "SQM only",
    description: "Unavailable for house PV: SQM has no confirmed property type",
  },
];

function formatNumberValue(value: number | null | undefined): string {
  if (value === null || value === undefined) {
    return "Any";
  }
  if (value >= 6) {
    return "6+";
  }
  return String(value);
}

function asNullableNumber(value: string): number | null {
  if (value === "Any") {
    return null;
  }
  return value === "6+" ? 6 : parseInt(value, 10);
}

function formatSuburb(suburb: SuburbRecord): string {
  return `${suburb.locality || suburb.suburb_name}, ${suburb.state} ${suburb.postcode}`;
}

function getSuburbName(suburb: SuburbRecord | null): string | null {
  return suburb?.locality || suburb?.suburb_name || null;
}

function formatCurrency(value: number | string | null | undefined): string {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) {
    return "—";
  }

  return new Intl.NumberFormat("en-AU", {
    style: "currency",
    currency: "AUD",
    maximumFractionDigits: 0,
  }).format(numeric);
}

function formatPercent(value: number | string | null | undefined): string {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) {
    return "—";
  }

  return `${numeric > 0 ? "+" : ""}${numeric.toFixed(2)}%`;
}

function formatMonth(value: string): string {
  return new Intl.DateTimeFormat("en-AU", { month: "short", year: "2-digit" }).format(
    new Date(value),
  );
}

function formatDateTime(value: string): string {
  return new Intl.DateTimeFormat("en-AU", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(new Date(value));
}

function formatCount(value: number | null | undefined): string {
  return Number.isFinite(value) ? Number(value).toLocaleString() : "0";
}

function getLatestResult(results: ValuationSuburbMonthlyResult[]) {
  return results.length ? results[results.length - 1] : null;
}

function TodoTab({ title }: { title: string }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
        <CardDescription>
          Stubbed intentionally. This will become a separate algorithm workspace with its own
          inputs, run history, QA checks, and result comparisons.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Badge variant="secondary">TODO</Badge>
      </CardContent>
    </Card>
  );
}

const chartConfig = {
  averagePrice: { label: "Average price", color: "hsl(var(--chart-1))" },
  percentageChange: { label: "MoM movement", color: "hsl(var(--chart-2))" },
};

const Valuations = () => {
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState("monthly");
  const [algorithms, setAlgorithms] = useState<ValuationAlgorithm[]>([DEFAULT_VALUATION_ALGORITHM]);
  const [selectedAlgorithm, setSelectedAlgorithm] = useState(DEFAULT_VALUATION_ALGORITHM.slug);
  const [setupWarning, setSetupWarning] = useState<string | null>(null);

  const [suburbSearch, setSuburbSearch] = useState("");
  const [suburbOptions, setSuburbOptions] = useState<SuburbRecord[]>([]);
  const [selectedSuburb, setSelectedSuburb] = useState<SuburbRecord | null>(null);
  const [isSearching, setIsSearching] = useState(false);

  const [bedrooms, setBedrooms] = useState("Any");
  const [bathrooms, setBathrooms] = useState("Any");
  const [parking, setParking] = useState("Any");
  const [months, setMonths] = useState("12");
  const [sourceMode, setSourceMode] = useState<ValuationSourceMode>("all");

  const [runs, setRuns] = useState<ValuationRun[]>([]);
  const [selectedRunId, setSelectedRunId] = useState<string | null>(null);
  const [results, setResults] = useState<ValuationSuburbMonthlyResult[]>([]);
  const [isLoadingRuns, setIsLoadingRuns] = useState(false);
  const [isLoadingResults, setIsLoadingResults] = useState(false);
  const [isRunning, setIsRunning] = useState(false);
  const [replayingRunId, setReplayingRunId] = useState<string | null>(null);
  const [deletingRunId, setDeletingRunId] = useState<string | null>(null);
  const [sourceCoverage, setSourceCoverage] = useState<ValuationSourceCoverage[]>([]);
  const [isLoadingCoverage, setIsLoadingCoverage] = useState(false);
  const [detailDrawerRunId, setDetailDrawerRunId] = useState<string | null>(null);
  const [traceDialog, setTraceDialog] = useState<{ runId: string; saleMonth: string } | null>(null);
  const { canManageJobs } = useAuth();

  const selectedRun = runs.find((run) => run.id === selectedRunId) ?? null;
  const selectedRunSqmCount = Number(
    (selectedRun?.result_summary as { sources?: { sqm_back_history?: unknown } } | undefined)
      ?.sources?.sqm_back_history ?? 0,
  );
  const selectedRunUsedUnconfirmedSqm =
    selectedRun !== null &&
    (selectedRunSqmCount > 0 || readSourceSelectionFromRun(selectedRun).mode === "sqm_only");
  const latestResult = getLatestResult(results);
  const totalSales = results.reduce((sum, result) => sum + result.sample_count, 0);
  const outliersRemoved = results.reduce((sum, result) => sum + result.outliers_removed, 0);

  const chartData = useMemo(
    () =>
      results.map((result) => ({
        month: formatMonth(result.sale_month),
        averagePrice: Number(result.average_price ?? 0),
        percentageChange: Number(result.percentage_change ?? 0),
      })),
    [results],
  );

  // Domain/REA suburb-profile comparison block lives on the selected run's
  // result_summary. We surface it as cards with EXPLICIT comparator labels
  // so it's obvious whether each number is a monthly average (algo),
  // single-month median (external), or rolling 12-month median (external).
  const comparison = useMemo(() => {
    const summary = selectedRun?.result_summary as
      | { domainProfileComparison?: unknown; windowRollup?: unknown }
      | undefined;
    const dpc = summary?.domainProfileComparison;
    if (!dpc || typeof dpc !== "object") return null;
    const block = dpc as {
      snapshotMonth?: string | null;
      domainMedianHousePrice?: number | null;
      domainAnnualGrowthPct?: number | null;
      domainHouseSalesVolume12mo?: number | null;
      algoComputedAnnualPct?: number | null;
      sourceUrl?: string | null;
      consensus?: {
        contributors?: string[];
        medianHousePrice?: number | null;
        annualGrowthPct?: number | null;
      } | null;
    };
    const wr = summary?.windowRollup as
      | {
          medianPrice?: number | null;
          meanPrice?: number | null;
          sampleCount?: number;
          months?: number;
        }
      | undefined;
    return { ...block, windowRollup: wr ?? null };
  }, [selectedRun]);

  const loadRuns = useCallback(async (currentSelectedRunId?: string | null) => {
    try {
      setIsLoadingRuns(true);
      const nextRuns = await fetchValuationRuns();
      setRuns(nextRuns);
      if (!currentSelectedRunId && nextRuns.length > 0) {
        setSelectedRunId(nextRuns[0].id);
      }
      setSetupWarning(null);
    } catch (err) {
      const message = err instanceof Error ? err.message : "Unable to load valuation runs";
      setSetupWarning(message);
      setRuns([]);
    } finally {
      setIsLoadingRuns(false);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function initialise() {
      try {
        const [nextAlgorithms, defaultSuburb] = await Promise.all([
          fetchValuationAlgorithms(),
          fetchDefaultValuationSuburb(),
        ]);
        if (cancelled) {
          return;
        }
        if (nextAlgorithms.length > 0) {
          setAlgorithms(nextAlgorithms);
          setSelectedAlgorithm(nextAlgorithms[0].slug);
        }
        if (defaultSuburb) {
          setSelectedSuburb(defaultSuburb);
          setSuburbSearch(formatSuburb(defaultSuburb));
          setSuburbOptions([defaultSuburb]);
        }
      } catch (err) {
        if (!cancelled) {
          const message = err instanceof Error ? err.message : "Valuation setup is not available";
          setSetupWarning(message);
        }
      }
    }

    initialise();
    loadRuns(null);

    return () => {
      cancelled = true;
    };
  }, [loadRuns]);

  useEffect(() => {
    if (!selectedRunId) {
      setResults([]);
      return;
    }

    const runId = selectedRunId;
    let cancelled = false;
    async function loadResults() {
      try {
        setIsLoadingResults(true);
        const nextResults = await fetchValuationRunResults(runId);
        if (!cancelled) {
          setResults(nextResults);
        }
      } catch (_err) {
        if (!cancelled) {
          setResults([]);
        }
      } finally {
        if (!cancelled) {
          setIsLoadingResults(false);
        }
      }
    }

    loadResults();
    return () => {
      cancelled = true;
    };
  }, [selectedRunId]);

  // Hydrate the run controls when a saved run is selected. Without this,
  // selecting Armadale-3yr from the history panel left the form on the
  // default suburb / 12mo / Any-filters — the McKinnon/Armadale mismatch
  // bug called out in the run lab plan.
  useEffect(() => {
    if (!selectedRunId) return;
    const run = runs.find((entry) => entry.id === selectedRunId);
    if (!run) return;
    setMonths(String(run.months));
    setBedrooms(formatNumberValue(run.bedrooms));
    setBathrooms(formatNumberValue(run.bathrooms));
    setParking(formatNumberValue(run.parking));
    setSourceMode(readSourceSelectionFromRun(run).mode);
    if (run.suburb_id) {
      const synthetic: SuburbRecord = {
        id: run.suburb_id,
        locality: run.suburb_name,
        suburb_name: run.suburb_name,
        postcode: run.postcode,
        state: run.state,
        region: null,
        // SuburbRecord includes a few optional fields; only the
        // identity fields drive the source-coverage query.
      } as SuburbRecord;
      setSelectedSuburb(synthetic);
      setSuburbSearch(formatSuburb(synthetic));
      setSuburbOptions([synthetic]);
    }
  }, [selectedRunId, runs]);

  useEffect(() => {
    const term = suburbSearch.trim();
    if (term.length < 2 || (selectedSuburb && term === formatSuburb(selectedSuburb))) {
      return;
    }

    const handle = window.setTimeout(async () => {
      try {
        setIsSearching(true);
        const nextOptions = await searchValuationSuburbs(term);
        setSuburbOptions(nextOptions);
      } catch (_err) {
        setSuburbOptions([]);
      } finally {
        setIsSearching(false);
      }
    }, 300);

    return () => window.clearTimeout(handle);
  }, [suburbSearch, selectedSuburb]);

  useEffect(() => {
    if (!selectedSuburb) {
      setSourceCoverage([]);
      return;
    }

    let cancelled = false;
    const handle = window.setTimeout(async () => {
      try {
        setIsLoadingCoverage(true);
        const coverage = await fetchValuationSourceCoverage({
          suburbId: selectedSuburb.id,
          suburbName: getSuburbName(selectedSuburb),
          postcode: selectedSuburb.postcode,
        });
        if (!cancelled) {
          setSourceCoverage(coverage);
        }
      } catch (_err) {
        if (!cancelled) {
          setSourceCoverage([]);
        }
      } finally {
        if (!cancelled) {
          setIsLoadingCoverage(false);
        }
      }
    }, 250);

    return () => {
      cancelled = true;
      window.clearTimeout(handle);
    };
  }, [selectedSuburb]);

  const handleRun = async () => {
    if (!selectedSuburb) {
      toast({
        title: "Select a suburb",
        description: "Choose a suburb/postcode before running the monthly movement algorithm.",
        variant: "destructive",
      });
      return;
    }

    try {
      setIsRunning(true);
      const response = await runMonthlyMovement({
        suburbId: selectedSuburb.id,
        months: parseInt(months, 10),
        bedrooms: asNullableNumber(bedrooms),
        bathrooms: asNullableNumber(bathrooms),
        parking: asNullableNumber(parking),
        sources: { mode: sourceMode },
      });

      if (!response.runId) {
        throw new Error("Valuation run did not return a runId");
      }

      await loadRuns(response.runId);
      setSelectedRunId(response.runId);
      toast({
        title: "Valuation run completed",
        description: `${formatSuburb(selectedSuburb)} monthly movement results were saved.`,
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : "Valuation run failed";
      toast({ title: "Valuation run failed", description: message, variant: "destructive" });
    } finally {
      setIsRunning(false);
    }
  };

  const handleReplayRun = async (runId: string) => {
    try {
      setReplayingRunId(runId);
      const response = await replayValuationRun({ runId });
      if (!response.runId) {
        throw new Error("Replay did not return a runId");
      }
      await loadRuns(response.runId);
      setSelectedRunId(response.runId);
      toast({
        title: "Replay completed",
        description: "A new run was created from the saved settings.",
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : "Replay failed";
      toast({ title: "Replay failed", description: message, variant: "destructive" });
    } finally {
      setReplayingRunId(null);
    }
  };

  const handleDeleteRun = async (runId: string) => {
    if (!canManageJobs) {
      toast({
        title: "Operator required",
        description: "Only admins or operators can delete valuation runs.",
        variant: "destructive",
      });
      return;
    }
    const confirmed = window.confirm(
      "Delete this valuation run? Results, source snapshots, and trace rows will be removed permanently.",
    );
    if (!confirmed) return;
    try {
      setDeletingRunId(runId);
      await deleteValuationRun(runId);
      const wasSelected = selectedRunId === runId;
      if (wasSelected) {
        setSelectedRunId(null);
        setResults([]);
      }
      await loadRuns(wasSelected ? null : selectedRunId);
      toast({ title: "Run deleted", description: "The valuation run was hard-deleted." });
    } catch (err) {
      const message = err instanceof Error ? err.message : "Delete failed";
      toast({ title: "Delete failed", description: message, variant: "destructive" });
    } finally {
      setDeletingRunId(null);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <h1 className="text-2xl font-bold">Valuations</h1>
          <p className="text-muted-foreground">
            Prototype PV algorithms from internal property snapshots, starting with suburb-level
            monthly house price movements.
          </p>
        </div>
        <Badge variant="outline" className="w-fit">
          Algorithm lab
        </Badge>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="h-auto flex-wrap justify-start">
          {WHOLE_PAGE_TABS.map((tab) => (
            <TabsTrigger key={tab.value} value={tab.value} className="gap-2">
              {tab.label}
              {tab.status === "todo" ? (
                <Badge variant="secondary" className="ml-1 text-[10px]">
                  TODO
                </Badge>
              ) : null}
            </TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="monthly" className="space-y-6">
          {setupWarning ? (
            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                Valuation persistence is not fully available yet: {setupWarning}. Apply the new
                migration and deploy the edge function before running the algorithm in this
                environment.
              </AlertDescription>
            </Alert>
          ) : null}

          <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_360px]">
            <Card>
              <CardHeader>
                <CardTitle>PV Suburb Movements Monthly</CardTitle>
                <CardDescription>
                  Select a suburb and comparable segment. The v1 algorithm uses house sale
                  snapshots, removes IQR outliers for groups with at least four sales, and compares
                  monthly average prices.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-5">
                <div className="grid gap-4 lg:grid-cols-[minmax(0,1.5fr)_minmax(220px,0.8fr)]">
                  <div className="space-y-2">
                    <Label htmlFor="suburb-search">Suburb or postcode</Label>
                    <div className="relative">
                      <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
                      <Input
                        id="suburb-search"
                        value={suburbSearch}
                        onChange={(event) => {
                          setSuburbSearch(event.target.value);
                          setSelectedSuburb(null);
                        }}
                        placeholder="Search McKinnon, 3204, Brighton..."
                        className="pl-9"
                      />
                    </div>
                    {suburbOptions.length > 0 && !selectedSuburb ? (
                      <div className="rounded-md border bg-background p-1 shadow-sm">
                        {suburbOptions.map((suburb) => (
                          <button
                            key={suburb.id}
                            type="button"
                            className="flex w-full items-center justify-between rounded-sm px-3 py-2 text-left text-sm hover:bg-muted"
                            onClick={() => {
                              setSelectedSuburb(suburb);
                              setSuburbSearch(formatSuburb(suburb));
                              setSuburbOptions([suburb]);
                            }}
                          >
                            <span>{formatSuburb(suburb)}</span>
                            {suburb.region ? (
                              <span className="text-xs text-muted-foreground">{suburb.region}</span>
                            ) : null}
                          </button>
                        ))}
                      </div>
                    ) : null}
                    {isSearching ? (
                      <p className="flex items-center gap-2 text-xs text-muted-foreground">
                        <Loader2 className="h-3 w-3 animate-spin" />
                        Searching suburbs...
                      </p>
                    ) : null}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="algorithm">Algorithm</Label>
                    <Select value={selectedAlgorithm} onValueChange={setSelectedAlgorithm}>
                      <SelectTrigger id="algorithm">
                        <SelectValue placeholder="Select algorithm" />
                      </SelectTrigger>
                      <SelectContent>
                        {algorithms.map((algorithm) => (
                          <SelectItem key={algorithm.slug} value={algorithm.slug}>
                            {algorithm.name} v{algorithm.version}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="grid gap-4 md:grid-cols-4">
                  <div className="space-y-2">
                    <Label htmlFor="bedrooms">Bedrooms</Label>
                    <Select value={bedrooms} onValueChange={setBedrooms}>
                      <SelectTrigger id="bedrooms">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {FILTER_OPTIONS.map((option) => (
                          <SelectItem key={option} value={option}>
                            {option}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="bathrooms">Bathrooms</Label>
                    <Select value={bathrooms} onValueChange={setBathrooms}>
                      <SelectTrigger id="bathrooms">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {FILTER_OPTIONS.map((option) => (
                          <SelectItem key={option} value={option}>
                            {option}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="parking">Car spaces</Label>
                    <Select value={parking} onValueChange={setParking}>
                      <SelectTrigger id="parking">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {FILTER_OPTIONS.map((option) => (
                          <SelectItem key={option} value={option}>
                            {option}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="months">Months</Label>
                    <Select value={months} onValueChange={setMonths}>
                      <SelectTrigger id="months">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {["6", "12", "24", "36", "60"].map((option) => (
                          <SelectItem key={option} value={option}>
                            Last {option}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="source-mode">Data sources</Label>
                  <Select
                    value={sourceMode}
                    onValueChange={(value) => setSourceMode(value as ValuationSourceMode)}
                  >
                    <SelectTrigger id="source-mode">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {SOURCE_MODE_OPTIONS.map((option) => (
                        <SelectItem
                          key={option.value}
                          value={option.value}
                          disabled={option.value === "sqm_only"}
                        >
                          <span className="font-medium">{option.label}</span>
                          <span className="ml-2 text-xs text-muted-foreground">
                            {option.description}
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {sourceMode === "sqm_only" ? (
                    <p className="text-sm text-muted-foreground">
                      SQM-only house PV is unavailable because SQM sales have no confirmed property
                      type. Choose another source mode to run a new valuation.
                    </p>
                  ) : null}
                </div>

                <div className="flex flex-col gap-3 rounded-lg border bg-muted/30 p-4 md:flex-row md:items-center md:justify-between">
                  <div>
                    <p className="text-sm font-medium">QA / acceptance criteria for v1</p>
                    <p className="text-sm text-muted-foreground">
                      Results must be scoped to houses, preserve sample counts, show outlier
                      removal, and save an immutable run before charting.
                    </p>
                  </div>
                  <Button
                    type="button"
                    onClick={handleRun}
                    disabled={isRunning || !selectedSuburb || sourceMode === "sqm_only"}
                  >
                    {isRunning ? (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    ) : (
                      <Play className="mr-2 h-4 w-4" />
                    )}
                    Run algorithm
                  </Button>
                </div>
              </CardContent>
            </Card>

            <div className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle>Saved runs</CardTitle>
                  <CardDescription>Latest persisted outputs for comparison.</CardDescription>
                </CardHeader>
                <CardContent className="space-y-3">
                  {isLoadingRuns ? (
                    <p className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Loading runs...
                    </p>
                  ) : null}
                  {!isLoadingRuns && runs.length === 0 ? (
                    <p className="text-sm text-muted-foreground">No valuation runs saved yet.</p>
                  ) : null}
                  {runs.map((run) => {
                    const isSelected = selectedRunId === run.id;
                    const isReplaying = replayingRunId === run.id;
                    const isDeleting = deletingRunId === run.id;
                    return (
                      <div
                        key={run.id}
                        className={`rounded-lg border p-3 transition ${
                          isSelected ? "border-primary bg-primary/5" : "bg-background"
                        }`}
                      >
                        <button
                          type="button"
                          onClick={() => setSelectedRunId(run.id)}
                          className="w-full text-left"
                        >
                          <div className="flex items-start justify-between gap-2">
                            <div>
                              <p className="font-medium">
                                {run.suburb_name}, {run.state} {run.postcode}
                              </p>
                              <p className="text-xs text-muted-foreground">
                                {formatDateTime(run.created_at)} · {run.months} months
                              </p>
                            </div>
                            <Badge variant={run.status === "completed" ? "default" : "secondary"}>
                              {run.status}
                            </Badge>
                          </div>
                          <p className="mt-2 text-xs text-muted-foreground">
                            Beds {run.bedrooms ?? "Any"} · Baths {run.bathrooms ?? "Any"} · Cars{" "}
                            {run.parking ?? "Any"} ·{" "}
                            <span className="font-medium text-foreground">
                              {readSourceSelectionFromRun(run).mode === "all"
                                ? "All sources"
                                : readSourceSelectionFromRun(run).mode === "internal_only"
                                  ? "Internal only"
                                  : readSourceSelectionFromRun(run).mode === "sqm_only"
                                    ? "SQM only"
                                    : "Adapter scope"}
                            </span>
                          </p>
                        </button>
                        <div className="mt-3 flex items-center justify-end gap-2">
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => setDetailDrawerRunId(run.id)}
                            disabled={isReplaying || isDeleting}
                          >
                            <Eye className="mr-1 h-3.5 w-3.5" />
                            Detail
                          </Button>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => handleReplayRun(run.id)}
                            disabled={isReplaying || isDeleting}
                          >
                            {isReplaying ? (
                              <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
                            ) : (
                              <RotateCw className="mr-1 h-3.5 w-3.5" />
                            )}
                            Replay
                          </Button>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteRun(run.id)}
                            disabled={isDeleting || isReplaying || !canManageJobs}
                            title={!canManageJobs ? "Operator role required" : undefined}
                          >
                            {isDeleting ? (
                              <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
                            ) : (
                              <Trash2 className="mr-1 h-3.5 w-3.5" />
                            )}
                            Delete
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <DatabaseZap className="h-5 w-5" />
                    Source coverage
                  </CardTitle>
                  <CardDescription>
                    Data currently available for the selected suburb/postcode.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-3">
                  {!selectedSuburb ? (
                    <p className="text-sm text-muted-foreground">
                      Select a suburb to inspect available PV inputs.
                    </p>
                  ) : null}
                  {isLoadingCoverage ? (
                    <p className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Checking source coverage...
                    </p>
                  ) : null}
                  {selectedSuburb && !isLoadingCoverage && sourceCoverage.length === 0 ? (
                    <p className="text-sm text-muted-foreground">
                      No source coverage returned yet. Apply the data-source migration and Edge
                      Function.
                    </p>
                  ) : null}
                  {sourceCoverage.map((source) => (
                    <div key={source.sourceSlug} className="rounded-lg border p-3">
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <p className="text-sm font-medium">{source.sourceName}</p>
                          <p className="text-xs text-muted-foreground">{source.coverageLabel}</p>
                        </div>
                        <Badge variant={source.status === "active" ? "default" : "secondary"}>
                          {source.status.replace(/_/g, " ")}
                        </Badge>
                      </div>
                      <div className="mt-3 grid grid-cols-2 gap-2 text-sm">
                        <div>
                          <p className="text-muted-foreground">Records</p>
                          <p className="font-semibold">{formatCount(source.recordCount)}</p>
                        </div>
                        <div>
                          <p className="text-muted-foreground">Priced</p>
                          <p className="font-semibold">
                            {formatCount(source.pricedRecordCount ?? source.recordCount)}
                          </p>
                        </div>
                      </div>
                      {source.notes ? (
                        <p className="mt-2 text-xs text-muted-foreground">{source.notes}</p>
                      ) : null}
                    </div>
                  ))}
                </CardContent>
              </Card>
            </div>
          </div>

          {selectedRunUsedUnconfirmedSqm ? (
            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                This saved run used SQM sales as houses before their property types were verified.
                Treat its PV figures as unconfirmed and rerun with confirmed scraped sales.
              </AlertDescription>
            </Alert>
          ) : null}

          <div className="grid gap-4 md:grid-cols-4">
            <Card>
              <CardHeader className="pb-2">
                <CardDescription>
                  Latest <span className="font-semibold">monthly average</span>
                </CardDescription>
                <CardTitle>{formatCurrency(latestResult?.average_price)}</CardTitle>
              </CardHeader>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardDescription>
                  Latest <span className="font-semibold">MoM</span> movement
                </CardDescription>
                <CardTitle>{formatPercent(latestResult?.percentage_change)}</CardTitle>
              </CardHeader>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardDescription>Months with data</CardDescription>
                <CardTitle>{results.length}</CardTitle>
              </CardHeader>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardDescription>Sales used / outliers</CardDescription>
                <CardTitle>
                  {totalSales} / {outliersRemoved}
                </CardTitle>
              </CardHeader>
            </Card>
          </div>

          {comparison ? (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Suburb-profile cross-check</CardTitle>
                <CardDescription>
                  External Domain/REA suburb-profile snapshots side-by-side with this run's output.
                  Each metric is explicitly labelled so the comparator is unambiguous.
                </CardDescription>
              </CardHeader>
              <CardContent className="grid gap-3 md:grid-cols-3">
                <div className="rounded-md border p-3">
                  <p className="text-xs font-medium uppercase text-muted-foreground">
                    External · <span className="text-foreground">single-month median</span>
                  </p>
                  <p className="mt-1 text-xl font-semibold">
                    {formatCurrency(comparison.domainMedianHousePrice)}
                  </p>
                  <p className="mt-1 text-xs text-muted-foreground">
                    Snapshot month {comparison.snapshotMonth ?? "—"}
                  </p>
                </div>
                {comparison.windowRollup ? (
                  <div className="rounded-md border p-3">
                    <p className="text-xs font-medium uppercase text-muted-foreground">
                      Algo ·{" "}
                      <span className="text-foreground">
                        rolling {comparison.windowRollup.months ?? "—"}-mo median
                      </span>
                    </p>
                    <p className="mt-1 text-xl font-semibold">
                      {formatCurrency(comparison.windowRollup.medianPrice)}
                    </p>
                    <p className="mt-1 text-xs text-muted-foreground">
                      n = {comparison.windowRollup.sampleCount ?? 0} sales · mean{" "}
                      {formatCurrency(comparison.windowRollup.meanPrice)}
                    </p>
                  </div>
                ) : null}
                <div className="rounded-md border p-3">
                  <p className="text-xs font-medium uppercase text-muted-foreground">
                    External · <span className="text-foreground">rolling 12-mo growth</span>
                  </p>
                  <p className="mt-1 text-xl font-semibold">
                    {/* `annual_growth_pct` is persisted as a percentage already
                       (3.4 = 3.4%), so render with `.toFixed(2)` and a literal %. */}
                    {comparison.domainAnnualGrowthPct !== null &&
                    comparison.domainAnnualGrowthPct !== undefined
                      ? `${Number(comparison.domainAnnualGrowthPct).toFixed(2)}%`
                      : "—"}
                  </p>
                  <p className="mt-1 text-xs text-muted-foreground">
                    Sales volume 12-mo: {comparison.domainHouseSalesVolume12mo ?? "—"}
                  </p>
                </div>
                <div className="rounded-md border p-3">
                  <p className="text-xs font-medium uppercase text-muted-foreground">
                    Algo · <span className="text-foreground">half-on-half median growth</span>
                  </p>
                  <p className="mt-1 text-xl font-semibold">
                    {/* algoComputedAnnualPct is a ratio (0.0573 = 5.73%) so multiply. */}
                    {comparison.algoComputedAnnualPct !== null &&
                    comparison.algoComputedAnnualPct !== undefined
                      ? `${(Number(comparison.algoComputedAnnualPct) * 100).toFixed(2)}%`
                      : "—"}
                  </p>
                  <p className="mt-1 text-xs text-muted-foreground">
                    First-half median → second-half median over {selectedRun?.months ?? "—"} mo
                  </p>
                </div>
                {comparison.consensus ? (
                  <div className="rounded-md border p-3 md:col-span-3">
                    <p className="text-xs font-medium uppercase text-muted-foreground">
                      Adapter consensus ·{" "}
                      <span className="text-foreground">single-month median</span>
                    </p>
                    <p className="mt-1 text-xl font-semibold">
                      {formatCurrency(comparison.consensus.medianHousePrice)}
                    </p>
                    <p className="mt-1 text-xs text-muted-foreground">
                      Sales-volume-weighted blend across:{" "}
                      {(comparison.consensus.contributors ?? []).join(", ") || "no contributors"}
                    </p>
                  </div>
                ) : null}
              </CardContent>
            </Card>
          ) : null}

          <div className="grid gap-4 xl:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <BarChart3 className="h-5 w-5" />
                  Average price by month
                </CardTitle>
                <CardDescription>
                  {selectedRun
                    ? `${selectedRun.suburb_name}, ${selectedRun.state} ${selectedRun.postcode}`
                    : "Select or run a valuation to chart results."}
                </CardDescription>
              </CardHeader>
              <CardContent>
                {isLoadingResults ? (
                  <p className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Loading results...
                  </p>
                ) : null}
                {!isLoadingResults && chartData.length === 0 ? (
                  <div className="flex min-h-[280px] items-center justify-center rounded-lg border border-dashed">
                    <p className="text-sm text-muted-foreground">No monthly result data yet.</p>
                  </div>
                ) : null}
                {chartData.length > 0 ? (
                  <ChartContainer config={chartConfig} className="min-h-[280px]">
                    <LineChart data={chartData} margin={{ left: 12, right: 12 }}>
                      <CartesianGrid vertical={false} />
                      <XAxis dataKey="month" tickLine={false} axisLine={false} tickMargin={8} />
                      <YAxis
                        tickLine={false}
                        axisLine={false}
                        tickFormatter={(value) => `$${Math.round(Number(value) / 1000)}k`}
                      />
                      <ChartTooltip content={<ChartTooltipContent />} />
                      <Line
                        type="monotone"
                        dataKey="averagePrice"
                        stroke="var(--color-averagePrice)"
                        strokeWidth={2}
                        dot={{ r: 3 }}
                      />
                    </LineChart>
                  </ChartContainer>
                ) : null}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Activity className="h-5 w-5" />
                  Month-on-month movement
                </CardTitle>
                <CardDescription>
                  Percentage change from the previous month with data.
                </CardDescription>
              </CardHeader>
              <CardContent>
                {chartData.length > 0 ? (
                  <ChartContainer config={chartConfig} className="min-h-[280px]">
                    <BarChart data={chartData} margin={{ left: 12, right: 12 }}>
                      <CartesianGrid vertical={false} />
                      <XAxis dataKey="month" tickLine={false} axisLine={false} tickMargin={8} />
                      <YAxis
                        tickLine={false}
                        axisLine={false}
                        tickFormatter={(value) => `${value}%`}
                      />
                      <ChartTooltip content={<ChartTooltipContent />} />
                      <Bar
                        dataKey="percentageChange"
                        fill="var(--color-percentageChange)"
                        radius={4}
                      />
                    </BarChart>
                  </ChartContainer>
                ) : (
                  <div className="flex min-h-[280px] items-center justify-center rounded-lg border border-dashed">
                    <p className="text-sm text-muted-foreground">No movement data yet.</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader>
              <CardTitle>Monthly result table</CardTitle>
              <CardDescription>
                Persisted output for the selected run, including sample size and IQR outlier count.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Month</TableHead>
                      <TableHead className="text-right">Average</TableHead>
                      <TableHead className="text-right">Median</TableHead>
                      <TableHead className="text-right">MoM</TableHead>
                      <TableHead className="text-right">Sample</TableHead>
                      <TableHead className="text-right">Outliers</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {results.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} className="py-8 text-center text-muted-foreground">
                          No results selected.
                        </TableCell>
                      </TableRow>
                    ) : null}
                    {results.map((result) => (
                      <TableRow
                        key={result.id}
                        className={selectedRunId ? "cursor-pointer hover:bg-muted/50" : undefined}
                        onClick={() => {
                          if (selectedRunId) {
                            setTraceDialog({ runId: selectedRunId, saleMonth: result.sale_month });
                          }
                        }}
                        title={selectedRunId ? "Click to trace this month's value" : undefined}
                      >
                        <TableCell>{formatMonth(result.sale_month)}</TableCell>
                        <TableCell className="text-right">
                          {formatCurrency(result.average_price)}
                        </TableCell>
                        <TableCell className="text-right">
                          {formatCurrency(result.median_price)}
                        </TableCell>
                        <TableCell className="text-right">
                          {formatPercent(result.percentage_change)}
                        </TableCell>
                        <TableCell className="text-right">
                          {result.sample_count} / {result.raw_sample_count}
                        </TableCell>
                        <TableCell className="text-right">{result.outliers_removed}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {WHOLE_PAGE_TABS.filter((tab) => tab.status === "todo").map((tab) => (
          <TabsContent key={tab.value} value={tab.value}>
            <TodoTab title={tab.label} />
          </TabsContent>
        ))}
      </Tabs>

      <RunDetailDrawer
        runId={detailDrawerRunId}
        open={detailDrawerRunId !== null}
        onOpenChange={(open) => {
          if (!open) setDetailDrawerRunId(null);
        }}
      />
      <TraceResultDialog
        runId={traceDialog?.runId ?? null}
        saleMonth={traceDialog?.saleMonth ?? null}
        open={traceDialog !== null}
        onOpenChange={(open) => {
          if (!open) setTraceDialog(null);
        }}
      />
    </div>
  );
};

export default Valuations;
